/**
 * A SIP trunk caller's number as E.164, read in the trunk's own country (2026-10-28, TRUNK-E164; design §40.26,
 * amending DD29-11 at the owner's request: a campaign's call-backs through a customer's trunk must match its lists).
 *
 * A PBX or a provider often presents its callers the way people dial inside the country — `6912345678`, `06912345678`,
 * `306912345678`, `00306912345678` — whatever the trunk's `wire_number_format` says about the numbers WeTalk DIALS.
 * `fromWireNumber` (strict, the trunk's format) is asked first and is unchanged; this module reads what it refused, and
 * only when the reading is UNAMBIGUOUS:
 *
 * | the caller on a GR trunk | E.164 | why |
 * |---|---|---|
 * | `+306912345678`, `+447700900123` | as given | already E.164 |
 * | `00306912345678`, `00447700900123` | `+30…`, `+44…` | the country's international prefix |
 * | `306912345678` | `+306912345678` | the calling code without `+` — the LENGTH decides, never shared leading digits |
 * | `6912345678`, `2101234567` | `+30…` | a national significant number (NSN) of the country's length |
 * | `06912345678` | `+306912345678` | GR has no trunk prefix: one leading `0` (a PBX's outside-line digit) before a full NSN |
 * | `201`, `69123`, `447700900123`, `anonymous`, `+30 69…` | — | an extension, too short, another country without `+`/`00`, not digits |
 *
 * A number in the trunk's own country must have the reviewed NSN length and a subscriber first digit; anything else is
 * `null`, and the leg keeps DD29-11's trunk wire digest. The country is the trunk's (`sipTrunkCallerCountryOf`); with no
 * single country there is nothing to read in, and the answer is `null`. No phone library: the reviewed rules
 * (`NATIONAL_NUMBER_RULE`, {@link CALLER_NUMBER_RULE}) are the only data.
 *
 * Pure and total. Never logs: a caller's number is party data.
 */
import { nationalNumberRuleOf } from "./national-number-rule.js";
import { isE164 } from "./wire-number.js";
export const CALLER_NUMBER_RULE = Object.freeze({
    /*
     * Greece. International prefix 00. A 10-digit NSN starts 2 (geographic), 4 (M2M 40–42), 5 (VPN 50), 6 (mobile
     * 69x, 685–689), 7 (personal numbers 70), 8 (freephone, shared cost and value added 8xx) or 9 (value added 90x);
     * range 1 holds only 3–6 digit short codes and 0 / 3 are not assigned. Source: EETT, national ITU-T E.164
     * numbering plan for +30, ITU-T communications of 9.IX.2021 and 17.IX.2021 (the same source as
     * `NATIONAL_NUMBER_RULE.GR` and `REVIEWED_DESTINATION_PREFIX.GR`).
     */
    GR: Object.freeze({
        internationalPrefix: "00",
        subscriberFirstDigit: "2456789",
        source: "EETT national ITU-T E.164 numbering plan for +30, ITU-T communications of 9.IX.2021 and 17.IX.2021",
        readAt: "2026-10-28",
    }),
});
/** The reviewed caller rule of `country`, or `undefined` (its callers are not read in). */
export function callerNumberRuleOf(country) {
    return Object.hasOwn(CALLER_NUMBER_RULE, country) ? CALLER_NUMBER_RULE[country] : undefined;
}
/** Whether a trunk's callers can be read in `country`: both reviewed rules exist. */
function readableCountry(country) {
    return nationalNumberRuleOf(country) !== undefined && callerNumberRuleOf(country) !== undefined;
}
/** The reviewed country an E.164 number belongs to (calling code AND NSN length), or `null`. */
function countryOfE164(e164) {
    for (const country of Object.keys(CALLER_NUMBER_RULE)) {
        const rule = nationalNumberRuleOf(country);
        if (rule === undefined)
            continue;
        const prefix = `+${rule.callingCode}`;
        if (e164.startsWith(prefix) && e164.length - prefix.length === rule.nationalSignificantLength)
            return country;
    }
    return null;
}
/**
 * The country a trunk's callers are read in, or `null` when no single one is derivable. In order, the first that
 * names exactly ONE country decides:
 *   1. `national_country`, when set (the member said so) — `null` when its callers cannot be read;
 *   2. `allowed_destination_country`, when it holds exactly one country — `null` when that one cannot be read;
 *   3. the trunk's own numbers, when every one belongs to the same reviewed country.
 * Several destination countries, or numbers in several (or unreviewed) countries, are no single country.
 */
export function sipTrunkCallerCountryOf(trunk) {
    if (trunk.nationalCountry !== null)
        return readableCountry(trunk.nationalCountry) ? trunk.nationalCountry : null;
    const destination = [...new Set(trunk.allowedDestinationCountry)];
    if (destination.length === 1) {
        const only = destination[0];
        return readableCountry(only) ? only : null;
    }
    if (trunk.numberE164.length === 0)
        return null;
    const country = new Set(trunk.numberE164.map(countryOfE164));
    if (country.size !== 1)
        return null;
    const [only] = country;
    return only === undefined ? null : only;
}
const DIGITS = /^[0-9]+$/;
/**
 * A trunk caller's wire value as E.164 in `country` (the header's table), or `null` when it is not unambiguously one.
 * `country` is {@link sipTrunkCallerCountryOf}'s answer; `null` reads nothing.
 */
export function sipTrunkCallerE164Of(wire, country) {
    if (country === null)
        return null;
    const rule = nationalNumberRuleOf(country);
    const caller = callerNumberRuleOf(country);
    if (rule === undefined || caller === undefined)
        return null;
    const ownCountryPrefix = `+${rule.callingCode}`;
    const subscriber = (nsn) => nsn.length === rule.nationalSignificantLength && DIGITS.test(nsn) && caller.subscriberFirstDigit.includes(nsn.charAt(0));
    /** An international reading: E.164, and a valid subscriber number when it is in the trunk's own country. */
    const international = (e164) => {
        if (!isE164(e164))
            return null;
        if (e164.startsWith(ownCountryPrefix) && !subscriber(e164.slice(ownCountryPrefix.length)))
            return null;
        return e164;
    };
    if (wire.startsWith("+"))
        return international(wire);
    if (!DIGITS.test(wire))
        return null;
    if (caller.internationalPrefix !== "" && wire.startsWith(caller.internationalPrefix)) {
        return international(`+${wire.slice(caller.internationalPrefix.length)}`);
    }
    const { callingCode, trunkPrefix } = rule;
    // The calling code without `+` or the international prefix: only at exactly the calling code + NSN length.
    if (wire.startsWith(callingCode) && subscriber(wire.slice(callingCode.length)))
        return `+${wire}`;
    // A national number with the country's trunk prefix.
    if (trunkPrefix !== "" && wire.startsWith(trunkPrefix) && subscriber(wire.slice(trunkPrefix.length))) {
        return `${ownCountryPrefix}${wire.slice(trunkPrefix.length)}`;
    }
    // A national significant number as it stands.
    if (subscriber(wire))
        return `${ownCountryPrefix}${wire}`;
    // A country with no trunk prefix: one leading `0` (a PBX's outside-line digit) before a full NSN.
    if (trunkPrefix === "" && wire.startsWith("0") && subscriber(wire.slice(1)))
        return `${ownCountryPrefix}${wire.slice(1)}`;
    return null;
}
//# sourceMappingURL=caller-number.js.map